import React, { memo, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { CancelToken, isCancel } from 'axios';

import CurrentOrgContext from '../../Context/CurrentOrgContext';
import SchedulesService from '../../Services/SchedulesService';
import AssetMaintenanceDisplay from './AssetMaintenanceDisplay';

export default memo(AssetMaintenanceContainer);

const initState = {
  assetReminderInstances: [],
  checkedInstances: new Map(),
  dataLoading: true,
  reminderNameSearch: '',
  scheduleNameSearch: '',
  typeFilter: ['complete', 'due', 'upcoming'],
};
function AssetMaintenanceContainer(props) {
  const { assetID } = useParams();
  const { _id: orgID } = useContext(CurrentOrgContext) || {};

  const [state, updateState] = useState(initState);
  const [page, updatePage] = useState(0);
  const [showServiceCompleteModal, toggleServiceCompleteModal] =
    useState(false);

  const source = CancelToken.source();

  const assetShadowSignalsMap = useMemo(() => {
    if (!props.asset.shadow?.signalCollections?.length) {
      return new Map();
    }

    const entries = props.asset.shadow.signalCollections.reduce(
      (acc, { signals = [] } = {}) => {
        if (!signals.length) return acc;
        signals.forEach(({ name, ...rest } = {}) => {
          acc.push([name, rest]);
        });
        return acc;
      },
      []
    );
    return new Map(entries);
  }, [props.asset.shadow?.signalCollections?.length]);

  const handlePropUpdate = ({ target: { name, value } } = {}) => {
    updateState({ ...state, [name]: value });
  };

  const handleTypeFilterSelect = ({ value }) => {
    const typeFilter = state.typeFilter.includes(value)
      ? state.typeFilter.filter((f) => f !== value)
      : state.typeFilter.concat(value);
    updateState({ ...state, typeFilter });
  };

  const handleInstanceChecked = ([id, instance]) => {
    const checkedInstances = new Map(state.checkedInstances);
    if (checkedInstances.has(id)) {
      checkedInstances.delete(id);
    } else {
      checkedInstances.set(id, instance);
    }
    updateState({ ...state, checkedInstances });
  };

  const handleSelectAllInstances = (instances, allChecked) => {
    const checkedInstances = allChecked ? new Map() : new Map(instances);
    updateState({ ...state, checkedInstances });
  };

  const fetchAssetInstances = async (
    filters = {},
    closeServiceCompleteModal = false
  ) => {
    try {
      const params = {
        orgID,
        children: true,
        gatewayIdFilter: assetID,
        ...filters,
      };
      const assetReminderInstances =
        await new SchedulesService().fetchReminderInstances(
          params,
          source.token
        );
      updateState({ ...state, assetReminderInstances, dataLoading: false });
      closeServiceCompleteModal &&
        toggleServiceCompleteModal(!showServiceCompleteModal);
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState({ ...state, error, dataLoading: false });
    }
  };

  const clearFilters = () => {
    updateState({
      ...state,
      reminderNameSearch: '',
      scheduleNameSearch: '',
      typeFilter: ['due', 'upcoming'],
    });
  };

  useEffect(() => {
    if (!orgID) return;
    fetchAssetInstances();
  }, [orgID]);

  return (
    <AssetMaintenanceDisplay
      {...state}
      assetShadowSignalsMap={assetShadowSignalsMap}
      clearFilters={clearFilters}
      fetchAssetInstances={fetchAssetInstances}
      handleInstanceChecked={handleInstanceChecked}
      handlePropUpdate={handlePropUpdate}
      handleSelectAllInstances={handleSelectAllInstances}
      handleTypeFilterSelect={handleTypeFilterSelect}
      page={page}
      showServiceCompleteModal={showServiceCompleteModal}
      toggleServiceCompleteModal={toggleServiceCompleteModal}
      updatePage={updatePage}
    />
  );
}

AssetMaintenanceContainer.propTypes = {
  asset: PropTypes.shape({
    _id: PropTypes.string,
    gtemplate: PropTypes.shape({
      datapoints: PropTypes.arrayOf(
        PropTypes.shape({
          _id: PropTypes.string,
          name: PropTypes.string,
          values: PropTypes.arrayOf(
            PropTypes.shape({ _id: PropTypes.string, name: PropTypes.string })
          ),
        })
      ),
    }),
    orgID: PropTypes.string,
    shadow: PropTypes.shape({
      signalCollections: PropTypes.arrayOf(
        PropTypes.shape({
          _id: PropTypes.string,
          signals: PropTypes.arrayOf(
            PropTypes.shape({
              _id: PropTypes.string,
              name: PropTypes.string,
              value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
              timestamp: PropTypes.number,
            })
          ),
        })
      ),
    }),
  }),
};

AssetMaintenanceContainer.defaultProps = { asset: {}, currentOrg: {} };
